import { ParagraphText, UBAppModal } from "../../../../Libraries";
import { useContext, useState, useEffect } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { Sync } from "../../../../SyncService";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { SuccessIcon } from "../../../../assets/icons/icon";
import styles from "./style.module.css";
import { Box, CircularProgress, Step, StepLabel, Stepper } from "@mui/material";
import FileUpload from "./FileUpload";
import StatusBox from "./StatusBox";
import Papa from "papaparse";
import { bulkCreateStockV2 } from "../../../../database/helpers/stock";
import { useAuthContext } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { bulkCreateInventoryV2 } from "../../../../database/helpers/inventory";
import { uuid } from "../../../../constants";
import { validString } from "../../../../utils";

const BulkUploadModal = ({ showModal, setShowModal, getInitialStock }) => {
  const { business, user } = useAuthContext();
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);
  const [file, setFile] = useState(null);
  const [analysisData, setAnalysedData] = useState({});
  const [bulkUploadItems, setBulkUploadItems] = useState([]);
  const [listingData, setListingData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImport, setIsImport] = useState(false);
  const { showSnackbar } = useSnackbar();

  const steps = [
    I18n.download_template || "Download the template",
    I18n.fill_in_your_data || "Fill in your data",
    I18n.upload_the_file || "Upload the file",
    I18n.import_items || "Import items",
  ];

  useEffect(() => {
    if (showModal) {
      triggerLogEvent(analytics, "sc_stock_bulk_modal_open", {});
    }
  }, [showModal]);

  const handleModalClose = () => {
    setShowModal(false);
    setAnalysedData({});
    setBulkUploadItems([]);
    setFile(null);
    setIsImport(false);
  };

  const analyzeCSVData = (fileData) => {
    let successfulEntries = [];
    let failedEntries = [];
    let bulkItems = [];
    let listings = [];
    Papa.parse(fileData, {
      complete: (result) => {
        try {
          result.data.forEach((row) => {
            const normalizedRow = Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key.toLowerCase(),
                value,
              ]),
            );

            let itemName =
              normalizedRow.name ||
              normalizedRow["item name"] ||
              normalizedRow["product name"];
            let costPrice =
              normalizedRow.cost_price ||
              normalizedRow["cost"] ||
              normalizedRow["cost price"];
            let stock = normalizedRow.stock || normalizedRow["quantity"];
            let image_url =
              normalizedRow.image_url ||
              normalizedRow["image url"] ||
              normalizedRow["image"];
            let unit_conversion = normalizedRow.unit_conversion;
            let selling_price =
              normalizedRow.selling_price ||
              normalizedRow["sell price"] ||
              normalizedRow["sell"];
            let barcode =
              normalizedRow.barcode || normalizedRow["product code"];
            let unit = normalizedRow.unit || normalizedRow["unit type"];
            let secondary_unit =
              normalizedRow.secondary_unit || normalizedRow["secondary unit"];
            let batch_number =
              normalizedRow.batch_number ||
              normalizedRow["batch number"] ||
              normalizedRow["batch"];
            let wholesale_price =
              normalizedRow.wholesale_price || normalizedRow["wholesale price"];
            let expiry_date =
              normalizedRow.expiry_date ||
              normalizedRow["expiration date"] ||
              normalizedRow["expiry date"];
            let manufacturing_date =
              normalizedRow.manufacturing_date ||
              normalizedRow["manufacture date"];
            let low_stock =
              normalizedRow.low_stock ||
              normalizedRow["low stock"] ||
              normalizedRow["stock low"];

            let params = {
              id: uuid(),
              name: itemName,
              cost_price: isNaN(Number(costPrice || 0)) ? 0 : Number(costPrice || 0),
              stock: isNaN(Number(stock || 1)) ? 0 : Number(stock || 1),
              image_url: image_url,
              unit_conversion: isNaN(Number(unit_conversion)) ? 0 : Number(unit_conversion),
              selling_price: isNaN(Number(selling_price || 0)) ? 0 : Number(selling_price || 0),
              barcode: isNaN(Number(barcode)) ? 0 : Number(barcode),
              unit: unit || "Pieces",
              secondary_unit: secondary_unit,
              batch_number: isNaN(Number(batch_number)) ? 0 : Number(batch_number),
              wholesale_price: isNaN(Number(wholesale_price)) ? 0 : Number(wholesale_price),
              expiry_date: expiry_date,
              manufacturing_date: manufacturing_date,
              low_stock: isNaN(Number(low_stock)) ? 0 : Number(low_stock),
              minimum_order_quantity: null,
              imei: null,
              tax: null,
            };

            listings.push(params);
            const valid = validString(itemName);
        
            if (itemName && valid) {
              successfulEntries.push(itemName);
              bulkItems.push(params);
            }
            if(!valid){
              let reason = I18n.bulk_upload_invalid_item_name;
              failedEntries.push({ item: itemName || "Unknown", reason });
            }
            else if (!itemName) {
              let reason = I18n.bulk_upload_item_name_missing;
              failedEntries.push({ item: itemName || "Unknown", reason });
            }
          });

          const analysisData = {
            fileName: fileData.name,
            successfulEntries: successfulEntries,
            failedEntries: failedEntries,
            status:
              successfulEntries.length > 0
                ? "Successful"
                : failedEntries.length > 0
                  ? "Failed"
                  : "No Data Found",
            successfulItems: successfulEntries.join(", "),
            failedReason: failedEntries.length ? failedEntries[0].reason : null,
          };
          setAnalysedData(analysisData);
          setBulkUploadItems(bulkItems);
          setListingData(listings);
          setIsImport(false);
        } catch (error) {
          console.error("Error processing CSV data:", error);
        }
      },
      header: true,
      skipEmptyLines: true,
    });
  };

  const handleSave = async () => {
    try {
      triggerLogEvent(analytics, "sc_stock_bulk_submit", {
        item_count: bulkUploadItems.length,
      });
      setIsLoading(true);
      let stockList = await bulkCreateStockV2(bulkUploadItems, business);
      await bulkCreateInventoryV2(bulkUploadItems, 'in');
      getInitialStock();
      Sync(user.phone_number, navigate);
      showSnackbar({
        message: `${bulkUploadItems.length} items added to stock book`,
        severity: "success",
      });
      setIsLoading(false);
      handleModalClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const templateUrl = "/templates/Stock_Items_Template.csv"; // Path to your template CSV
  const hasAnalysis = Object.values(analysisData).length > 0;
  const activeStep = isImport ? 3 : file ? 2 : 0;

  return (
    <UBAppModal
      id="bulk_upload_modal"
      className={styles.bulkUploadModal}
      open={showModal}
      header={{
        title: isImport ? "items_imported" : "bulk_upload",
        handleClose: () => setShowModal(false),
        dont_show_cross_icon: true,
      }}
      footer={[
        {
          data: {
            className: `ubSecondaryButton`,
            title: `${I18n.cancel}`,
            onClick: () => handleModalClose(),
            disabled: false,
            loading: false,
          },
        },
        {
          data: {
            className: `ubPrimaryButton`,
            title: isImport
              ? `${(I18n.save || "Save").toUpperCase()} ${bulkUploadItems.length} ${(I18n.items || "Items").toUpperCase()}`
              : (I18n.import_items || "Import items").toUpperCase(),
            loading: isLoading,
            onClick: () => (isImport ? handleSave() : setIsImport(true)),
            disabled: isImport
              ? !(bulkUploadItems.length > 0) || isLoading
              : !hasAnalysis || isLoading,
          },
        },
      ]}
    >
      <Box
        className={`${styles.modalBody} ${isImport ? styles.modalBodyImport : styles.modalBodyUpload}`}
      >
        {isImport ? (
          hasAnalysis && (
            <StatusBox
              analysisData={analysisData}
              bulkUploadItems={listingData}
            />
          )
        ) : (
          <>
            <ParagraphText className={styles.subtitle}>
              {I18n.upload_stock_items}
            </ParagraphText>
            <Stepper
              className={styles.bulkUploadStepper}
              activeStep={activeStep}
              alternativeLabel
            >
              {steps.map((label, index) => (
                <Step key={label} completed={false}>
                  <StepLabel>
                    {index === 0 ? (
                      <a
                        href={templateUrl}
                        download
                        className={styles.downloadTemplateLink}
                      >
                        <span
                          className={`${styles.stepLabelText} ${styles.downloadStep}`}
                        >
                          {label}
                        </span>
                      </a>
                    ) : (
                      <span className={styles.stepLabelText}>{label}</span>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <ParagraphText className={styles.uploadFileTitle}>
              {I18n.upload_file}
            </ParagraphText>

            <FileUpload
              file={file}
              setFile={setFile}
              analyzeData={analyzeCSVData}
              reset={() => {
                setAnalysedData({});
                setBulkUploadItems([]);
              }}
            />
          </>
        )}
      </Box>
    </UBAppModal>
  );
};

export default BulkUploadModal;
